/**
 * CapturedPieces React Component
 * 
 * Displays captured pieces for both players, organized by type,
 * with material advantage calculation.
 * 
 * Requirements: 21.1, 21.2, 21.3, 21.4
 */

import React from 'react';
import { ChessPiece, PlayerColor } from '../types';
import './CapturedPieces.css';

export interface CapturedPiecesProps {
  capturedPieces: {
    white: ChessPiece[];
    black: ChessPiece[];
  };
  orientation?: PlayerColor;
}

/**
 * Piece values for material advantage calculation
 */
const PIECE_VALUES: Record<string, number> = {
  pawn: 1,
  knight: 3,
  bishop: 3,
  rook: 5,
  queen: 9,
  king: 0 // King is never captured
};

/**
 * CapturedPieces component displays captured pieces and material advantage
 */
export const CapturedPieces: React.FC<CapturedPiecesProps> = ({
  capturedPieces,
  orientation = 'white'
}) => {
  /**
   * Renders a chess piece using Unicode symbols
   */
  const renderPiece = (piece: ChessPiece): string => {
    const pieceSymbols: Record<string, Record<string, string>> = {
      white: {
        king: '♔',
        queen: '♕',
        rook: '♖',
        bishop: '♗',
        knight: '♘',
        pawn: '♙'
      },
      black: {
        king: '♚',
        queen: '♛',
        rook: '♜',
        bishop: '♝',
        knight: '♞',
        pawn: '♟'
      }
    };

    return pieceSymbols[piece.color][piece.type] || '';
  };

  /**
   * Organizes captured pieces by type
   * Requirement 21.2: Organize pieces by type
   */
  const organizePiecesByType = (pieces: ChessPiece[]): ChessPiece[] => {
    // Sort order: queen, rook, bishop, knight, pawn
    const typeOrder: Record<string, number> = {
      queen: 0,
      rook: 1,
      bishop: 2,
      knight: 3,
      pawn: 4
    };

    return [...pieces].sort((a, b) => {
      const orderA = typeOrder[a.type] ?? 999;
      const orderB = typeOrder[b.type] ?? 999;
      return orderA - orderB;
    });
  };

  /**
   * Calculates material advantage
   * Requirement 21.3: Show material advantage calculation
   */
  const calculateMaterialAdvantage = (): {
    player: PlayerColor | null;
    advantage: number;
  } => {
    // Calculate total value of captured pieces for each player
    const whiteValue = capturedPieces.white.reduce(
      (sum, piece) => sum + PIECE_VALUES[piece.type],
      0
    );
    const blackValue = capturedPieces.black.reduce(
      (sum, piece) => sum + PIECE_VALUES[piece.type],
      0
    );

    // Determine who has the advantage
    const difference = whiteValue - blackValue;
    
    if (difference > 0) {
      return { player: 'white', advantage: difference };
    } else if (difference < 0) {
      return { player: 'black', advantage: Math.abs(difference) };
    } else {
      return { player: null, advantage: 0 };
    }
  };

  /**
   * Renders captured pieces for a player
   * Requirement 21.1: Display all captured pieces for both players
   * Requirement 21.5: Use same visual style as pieces on board
   */
  const renderCapturedPiecesForPlayer = (
    player: PlayerColor,
    pieces: ChessPiece[]
  ): JSX.Element => {
    const organizedPieces = organizePiecesByType(pieces);
    const materialAdvantage = calculateMaterialAdvantage();
    const hasAdvantage = materialAdvantage.player === player && materialAdvantage.advantage > 0;

    return (
      <div className={`captured-pieces__player captured-pieces__player--${player}`}>
        <div className="captured-pieces__label">
          {player === 'white' ? 'White' : 'Black'} Captured
          {hasAdvantage && (
            <span className="captured-pieces__advantage">
              +{materialAdvantage.advantage}
            </span>
          )}
        </div>
        <div className="captured-pieces__pieces">
          {organizedPieces.length === 0 ? (
            <span className="captured-pieces__empty">None</span>
          ) : (
            organizedPieces.map((piece, index) => (
              <span
                key={`${piece.type}-${index}`}
                className={`captured-pieces__piece captured-pieces__piece--${piece.color}`}
                title={`${piece.color} ${piece.type}`}
              >
                {renderPiece(piece)}
              </span>
            ))
          )}
        </div>
      </div>
    );
  };

  // Determine display order based on orientation
  const topPlayer = orientation === 'white' ? 'black' : 'white';
  const bottomPlayer = orientation === 'white' ? 'white' : 'black';

  return (
    <div className="captured-pieces">
      {renderCapturedPiecesForPlayer(topPlayer, capturedPieces[topPlayer])}
      {renderCapturedPiecesForPlayer(bottomPlayer, capturedPieces[bottomPlayer])}
    </div>
  );
};

export default CapturedPieces;
